import type { IFilesystem } from '@livx.cc/wcli/core';

const DEFAULT_NAMES = ['AGENT.md', 'AGENTS.md', 'CLAUDE.md'];

/**
 * Walk the VFS directory tree from `/` down, collecting instruction files
 * (AGENT.md, AGENTS.md, CLAUDE.md, .claude/<name>) at each level.
 * Merges all found files general-first → specific-last (mirrors Claude Code's
 * hierarchical CLAUDE.md discovery). First matching filename per directory wins
 * (no duplicates from the same level). Edge-portable: works over any IFilesystem.
 */
export async function loadInstructions(
  fs: IFilesystem,
  names: string[] = DEFAULT_NAMES,
): Promise<string> {
  // First matching name wins (AGENT.md > AGENTS.md > CLAUDE.md).
  // Both root and .claude/ locations are checked for the winning name — anchored at the FS
  // working dir (in CC-parity disk mode root '/' is the real machine, so instructions live
  // under the launch dir, not '/'). With cwd '/' this is the original behavior.
  const cwd = fs.getCwd();
  const base = cwd === '/' ? '' : cwd;
  for (const name of names) {
    const sections: string[] = [];
    for (const path of [`${base}/${name}`, `${base}/.claude/${name}`]) {
      if (!(await fs.exists(path))) continue;
      const md = (await fs.readFile(path)).trim();
      if (md) sections.push(`<!-- ${path} -->\n${md}`);
    }
    if (sections.length) {
      return `## Project instructions\nRepository-specific instructions — follow them.\n\n${sections.join('\n\n---\n\n')}`;
    }
  }
  return '';
}

/** An extra file injected into the prompt next to the instructions (e.g. a kb/user-space note). */
export interface ContextFileSpec { path: string; maxChars?: number; label?: string }

/**
 * Load explicit context files, same section format as instructions. Missing/empty → skipped;
 * ≤ `maxChars` → full; over → head + a pointer to the full file (the agent can Read the rest).
 */
export async function loadContextFiles(fs: IFilesystem, specs: ContextFileSpec[] = []): Promise<string> {
  const sections: string[] = [];
  for (const { path, maxChars, label } of specs) {
    if (!(await fs.exists(path))) continue;
    let md = (await fs.readFile(path)).trim();
    if (!md) continue;
    if (maxChars !== undefined) {
      const cps = [...md], n = Math.max(0, maxChars); // code points: never split a surrogate pair
      if (cps.length > n) md = `${cps.slice(0, n).join('').trimEnd()}\n… (truncated — full file: ${path})`;
    }
    sections.push(`<!-- ${path} -->\n${label ? `### ${label}\n` : ''}${md}`);
  }
  return sections.length ? `## Context files\n\n${sections.join('\n\n---\n\n')}` : '';
}
